import { useTranslation } from "react-i18next";
import { RefreshCwIcon, TriangleAlertIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  isBannerVisible,
  snoozeUpdate,
  useAppUpdateState,
} from "@/services/app-update/update-state";

/**
 * In-flow bar announcing a newer deploy. It never reloads by itself: the
 * soft (`available`) variant can be snoozed and comes back later; the
 * `required` variant cannot be snoozed. The refresh button reloads, and the
 * unsaved-changes registry arms the browser's leave-page prompt while a
 * form holds input, so the click cannot silently drop it.
 */
export function AppUpdateBanner() {
  const { t } = useTranslation("common");
  const state = useAppUpdateState();
  if (!isBannerVisible(state)) return null;

  const required = state.status === "required";
  const Icon = required ? TriangleAlertIcon : RefreshCwIcon;

  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2 text-sm md:px-6",
        required
          ? "border-warning/32 bg-warning/8 text-warning-foreground"
          : "border-[var(--info-line)] bg-[var(--info-soft)] text-[var(--info-ink)]",
      )}
      data-status={state.status}
      data-test="app-update-banner"
      role={required ? "alert" : "status"}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      <p className="min-w-0 flex-1">
        <span className="font-semibold">
          {t(required ? "appUpdate.requiredTitle" : "appUpdate.title")}
        </span>{" "}
        {t(required ? "appUpdate.requiredBody" : "appUpdate.body")}
      </p>
      <div className="flex shrink-0 items-center gap-2">
        {!required && (
          <Button
            data-test="app-update-later"
            onClick={() => snoozeUpdate()}
            size="sm"
            variant="ghost"
          >
            {t("appUpdate.later")}
          </Button>
        )}
        <Button
          data-test="app-update-refresh"
          onClick={() => globalThis.location.reload()}
          size="sm"
        >
          {t("buttons.refreshPage")}
        </Button>
      </div>
    </div>
  );
}
